import { StateError } from "@p0security/shared/types/error";

import type {
  PostgresLegacyAwsRdsConfig,
  PostgresLegacyGcpCloudSqlConfig,
} from "./types.ts";

export enum PostgresLegacyServiceAccountExtension {
  CLOUD_SQL = ".gserviceaccount.com",
}

export const gcpCloudSqlToInstanceName = (
  config: PostgresLegacyGcpCloudSqlConfig
) => {
  const { projectId: parent, region, instanceId } = config;
  return `${parent}:${region}:${instanceId}`;
};

export const awsRdsToInstanceName = (config: PostgresLegacyAwsRdsConfig) => {
  const { account, region, instance } = config;
  return `${account}:${region}:${instance}`;
};

const instanceErrorMessage = (instance: string) =>
  `The selected instance, ${instance}, cannot be parsed. Please contact your administrator.`;

/**
 * Gets an instance name from a postgres key in the format
 * {provider-type}/{parent}/{parent}:{region}:{instanceName}/{databaseName}
 */
export const toPostgresLegacyInstanceProvider = (
  instance: string
): "cloud-sql" | "rds" => {
  const provider = instance.split("/").at(0);
  if (!provider || (provider !== "cloud-sql" && provider !== "rds")) {
    throw new StateError({ instance }, instanceErrorMessage(instance));
  }
  return provider;
};

/**
 * Gets an instance name from a postgres key in the format
 * {provider-type}/{parent}/{parent}:{region}:{instanceName}/{databaseName}
 */
export const toPostgresLegacyInstanceName = (instance: string): string => {
  const instanceName = instance.split("/").at(-2)?.split(":").pop();
  if (!instanceName) {
    throw new StateError({ instance }, instanceErrorMessage(instance));
  }
  return instanceName;
};

/**
 * Gets a project id from a postgres key in the format
 * {provider-type}/{parent}/{parent}:{region}:{instanceName}/{databaseName}
 */
export const toPostgresLegacyParent = (instance: string): string => {
  const parent = instance.split("/").at(1);
  if (!parent) {
    throw new StateError({ instance }, instanceErrorMessage(instance));
  }
  return parent;
};

/**
 * Gets a database name from a postgres key in the format
 * {provider-type}/{parent}/{parent}:{region}:{instanceName}/{databaseName}
 */
export const toPostgresLegacyDatabaseName = (instance: string): string => {
  const databaseName = instance.split("/").pop();
  if (!databaseName) {
    throw new StateError({ instance }, instanceErrorMessage(instance));
  }
  return databaseName;
};

/**
 * Gets an instance region from a postgres key in the format
 * {provider-type}/{parent}/{parent}:{region}:{instanceName}/{databaseName}
 */
export const toPostgresLegacyInstanceRegion = (instance: string): string => {
  const region = instance.split("/").at(-2)?.split(":").at(1);
  if (!region) {
    throw new Error(
      `Cannot parse region. Invalid Postgres instance format: ${instance}`
    );
  }
  return region;
};
